/**
 * ⚠️ ВРЕМЕННОЕ РЕШЕНИЕ.
 *
 * Позволяет по query-параметру `?apiurl=indelsb` динамически переключить запросы на тестовое
 * окружение indels-b вместо дефолтных `VITE_API_URL` (payment API) и `VITE_DID_URL` (DID-сервис).
 * Нужно для тестирования функционала drivee-id на проде без пересборки/редеплоя.
 *
 * После завершения тестирования этот модуль и его использование в `axios-client.ts`, `main.tsx`
 * и `auth.ts` (везде, где вызываются `resolveApiBaseUrl()` / `resolveDidBaseUrl()`) следует
 * удалить и вернуть прямое обращение к `__GLOBAL__.VITE_API_URL` / `__GLOBAL__.VITE_DID_URL`.
 */

const API_URL_QUERY_PARAM = 'apiurl'
const DEFAULT_TARGET = 'default'
const STORAGE_KEY = 'api-url-target'

/** Именованные альтернативные окружения: payment API + DID-сервис. Расширяется по мере надобности. */
const TARGETS: Record<string, { api: string; did: string }> = {
    indelsb: {
        api: 'https://payment.indels-b.app',
        did: 'https://indels-b.app',
    },
}

const readStoredTarget = (): null | string => {
    try {
        return globalThis.sessionStorage.getItem(STORAGE_KEY)
    } catch {
        return null
    }
}

const storeTarget = (target: string): void => {
    try {
        globalThis.sessionStorage.setItem(STORAGE_KEY, target)
    } catch {
        // sessionStorage может быть недоступен (приватный режим) — игнорируем
    }
}

const clearStoredTarget = (): void => {
    try {
        globalThis.sessionStorage.removeItem(STORAGE_KEY)
    } catch {
        // no-op
    }
}

/**
 * Активная цель (имя окружения) с учётом query-селектора `?apiurl=<target>` и сохранённого выбора.
 *
 * Приоритеты:
 * - `?apiurl=default` — сброс на дефолт, очищает сохранённый выбор → `null`;
 * - `?apiurl=<known>` — известное окружение, запоминается на время вкладки (sessionStorage);
 * - параметра нет (или неизвестный) — берётся сохранённый на сессию выбор;
 * - иначе — `null` (дефолт).
 *
 * Сайд-эффект: обновляет/очищает `sessionStorage`.
 */
const resolveTarget = (): null | string => {
    if (globalThis.window === undefined) {
        return null
    }

    const parameter = new URLSearchParams(globalThis.location.search).get(API_URL_QUERY_PARAM)

    // Явный сброс на дефолтное окружение
    if (parameter === DEFAULT_TARGET) {
        clearStoredTarget()

        return null
    }

    // Явный выбор известного окружения — включаем и запоминаем на сессию
    if (parameter && TARGETS[parameter]) {
        storeTarget(parameter)

        return parameter
    }

    // Параметра нет (или неизвестный) — берём сохранённый на сессию выбор
    const stored = readStoredTarget()

    if (stored && TARGETS[stored]) {
        return stored
    }

    return null
}

/** Базовый URL payment API с учётом переключателя `?apiurl=<target>`. */
export const resolveApiBaseUrl = (): string => {
    const target = resolveTarget()

    return target ? TARGETS[target].api : __GLOBAL__.VITE_API_URL
}

/** Базовый URL DID-сервиса с учётом переключателя `?apiurl=<target>`. */
export const resolveDidBaseUrl = (): string => {
    const target = resolveTarget()

    return target ? TARGETS[target].did : __GLOBAL__.VITE_DID_URL
}
